import {
  registerUser,
  trackEvent,
  trackScreenView,
  useDispatch,
  useSelector,
} from '@bonanza-social/toolkit/state';
import {
  fetchRegistrationConfig,
  fetchRegistrationOffers,
  selectFirstErroredStepIndex,
  selectRegistrationAutoLoginState,
  selectRegistrationConfig,
  selectRegistrationConfigDataState,
  selectRegistrationFormData,
  selectRegistrationFormDataState,
  selectRegistrationFormSubmitError,
  selectRegistrationOffersDataState,
  selectRegistrationStepIndex,
  selectShouldReturnToSummary,
  setMarketingOptIn,
  setPreventRemove,
  setStepIndex,
  submitRegistrationForm,
} from '../../state';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { BonanzaCoinLoader, Box } from '@bonanza-social/moonlight';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import { BottomSheet, Modal } from '@bonanza-social/navigation';
import { ApiError } from '@bonanza-social/toolkit/api';
import RegistrationLoader from '../RegistrationLoader/RegistrationLoader';
import RegistrationFailed from '../RegistrationFailed/RegistrationFailed';
import {
  ALLOWED_NAVIGATION_URLS,
  ERROR_BOTTOM_SHEETS,
  MARKETING_OPT_OUT_MODAL_SHOWN_STORAGE_KEY,
} from './Registration.constants';
import { useBlocker } from 'react-router-dom';

function Registration() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formDataState = useSelector(selectRegistrationFormDataState);
  const submitErrorCode = useSelector(selectRegistrationFormSubmitError);
  const shouldReturnToSummary = useSelector(selectShouldReturnToSummary);
  const config = useSelector(selectRegistrationConfig);
  const configDataState = useSelector(selectRegistrationConfigDataState);
  const data = useSelector(selectRegistrationFormData);
  const offersDataState = useSelector(selectRegistrationOffersDataState);
  const stepIndex = useSelector(selectRegistrationStepIndex);
  const firstErroredStepIndex = useSelector(selectFirstErroredStepIndex);
  const step = useMemo(
    () => config.steps[stepIndex],
    [stepIndex, config.steps],
  );
  const autoLoginState = useSelector(selectRegistrationAutoLoginState);
  const [marketingOptOutModalShown, setMarketingOptOutModalShown] =
    useState(false);

  const shouldBlockNavigation = useMemo(() => {
    return !(autoLoginState === 'fulfilled' && formDataState === 'fulfilled');
  }, [autoLoginState, formDataState]);

  const blocker = useBlocker(shouldBlockNavigation);

  const getFirstInvalidStepIndex = useCallback(() => {
    return firstErroredStepIndex;
  }, [firstErroredStepIndex]);

  useEffect(() => {
    const getMarketingOptOutModalShown = async (): Promise<void> => {
      const result = await AsyncStorage.getItem(
        MARKETING_OPT_OUT_MODAL_SHOWN_STORAGE_KEY,
      );

      setMarketingOptOutModalShown(result === 'true');
    };

    getMarketingOptOutModalShown();
  }, []);

  const openMarketingOptOutModal = useCallback(
    (nextStepIndex?: number) => {
      Modal.open({
        namespace: 'registration',
        type: 'MarketingOptOutModal',
        options: {
          hideHeader: true,
          variant: 'default',
        },
        data: {
          onOptIn: () => {
            dispatch(setMarketingOptIn(true));

            if (nextStepIndex) {
              dispatch(setStepIndex(nextStepIndex));
            }
          },
          onOptOut: () => {
            dispatch(setMarketingOptIn(false));

            if (nextStepIndex) {
              dispatch(setStepIndex(nextStepIndex));
            }
          },
        },
        canClose: false,
        style: {
          width: {
            mobile: '90%',
            tablet: 480,
            desktop: 480,
          },
          height: {
            mobile: 'auto',
            tablet: 'auto',
            desktop: 'auto',
          },
          paddingTop: 'space-200',
          backgroundColor: 'screenBackground',
        },
      });
    },
    [dispatch],
  );

  const submitForm = useCallback(async () => {
    if (!data.marketing && !marketingOptOutModalShown) {
      openMarketingOptOutModal();
      setMarketingOptOutModalShown(true);

      return;
    }

    const actionResult = await dispatch(submitRegistrationForm(data));

    if (submitRegistrationForm.fulfilled.match(actionResult)) {
      const result = actionResult.payload;
      const { codeVerifier, authorizationCode } = result.account;
      dispatch(registerUser({ codeVerifier, authorizationCode }));

      return;
    }

    if (!submitRegistrationForm.rejected.match(actionResult)) return;

    const error = actionResult.payload;
    if (!(error instanceof ApiError)) return;

    const { errorCode } = error?.data || {};
    if (!errorCode) return;

    const bottomSheetConfig =
      errorCode in ERROR_BOTTOM_SHEETS
        ? ERROR_BOTTOM_SHEETS[errorCode as keyof typeof ERROR_BOTTOM_SHEETS]
        : null;

    if (bottomSheetConfig) {
      BottomSheet.open(bottomSheetConfig);
    } else {
      BottomSheet.open({
        namespace: 'common',
        type: 'Info',
        data: {
          textNS: 'registration',
          textKey: 'defaultErrorBottomSheetText',
        },
      });
    }
  }, [data, marketingOptOutModalShown, dispatch, openMarketingOptOutModal]);

  const returnToSummary = useCallback(() => {
    const summaryStepIndex = config.steps.findIndex(
      (step) => step.id === 'summary',
    );

    if (summaryStepIndex !== -1) {
      dispatch(setStepIndex(summaryStepIndex));
    }
  }, [config.steps, dispatch]);

  const onStepSubmit = useCallback(() => {
    const shouldShowMarketingOptOutModal =
      step.id === 'terms' && !data.marketing && !marketingOptOutModalShown;

    const nextStepIndex = config.steps
      .slice(stepIndex + 1)
      .findIndex((step) => !step.skip);

    const nextStepNavigationIndex = stepIndex + 1 + nextStepIndex;

    if (shouldShowMarketingOptOutModal) {
      openMarketingOptOutModal(nextStepNavigationIndex);
      setMarketingOptOutModalShown(true);

      return;
    }

    if (stepIndex < config.steps.length - 1) {
      dispatch(setStepIndex(nextStepNavigationIndex));
    } else {
      submitForm();
    }
  }, [
    step.id,
    data.marketing,
    marketingOptOutModalShown,
    stepIndex,
    config.steps,
    openMarketingOptOutModal,
    dispatch,
    submitForm,
  ]);

  useEffect(() => {
    dispatch(fetchRegistrationConfig());
    dispatch(fetchRegistrationOffers());
  }, [dispatch]);

  useEffect(() => {
    if (
      configDataState === 'fulfilled' &&
      offersDataState.match('fulfilled|rejected')
    ) {
      const index = getFirstInvalidStepIndex();

      dispatch(setStepIndex(index));
    }
  }, [configDataState, offersDataState, dispatch]);

  useEffect(() => {
    dispatch(setPreventRemove(true));
  }, [dispatch]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const targetPath = blocker.location?.pathname;
      if (targetPath && ALLOWED_NAVIGATION_URLS.includes(targetPath)) {
        blocker.proceed();
      } else {
        if (stepIndex !== 0) {
          const previousStepIndex = stepIndex - 1;
          dispatch(setStepIndex(previousStepIndex));
        } else {
          BottomSheet.open({
            namespace: 'registration',
            type: 'QuitRegistrationBottomSheet',
          });
        }

        blocker.reset();
      }
    }
  }, [blocker, blocker.state, dispatch]);

  useEffect(() => {
    if (configDataState !== 'fulfilled' || !step) return;
    dispatch(
      trackScreenView({
        screenClass: 'registration',
        screenName: step.id,
        screenParams: step,
      }),
    );

    dispatch(
      trackEvent({
        name: 'registration_step',
        params: {
          stepId: step.id,
          stepFields: step.fields?.join(','),
        },
      }),
    );
  }, [step, dispatch, configDataState]);

  if (
    configDataState.match('idle|pending') ||
    offersDataState.match('idle|pending') ||
    !step
  ) {
    return <BonanzaCoinLoader />;
  }

  if (autoLoginState === 'pending' || formDataState === 'pending') {
    return (
      <RegistrationLoader
        titleNS="registration"
        titleKey="registrationLoaderTitle"
      />
    );
  }

  if (autoLoginState === 'fulfilled' && formDataState === 'fulfilled') {
    navigate('/', { replace: true });
    return null;
  }

  if (submitErrorCode === 'account_suspended') {
    return <RegistrationFailed />;
  }

  return (
    <Box flex={1}>
      <RegistrationForm
        config={config}
        activeStepId={step.id}
        onStepSubmit={shouldReturnToSummary ? returnToSummary : onStepSubmit}
        submitDataState={formDataState}
      />
    </Box>
  );
}

export default memo(Registration);
